/**
 * Page Prix malbouffe de la semaine
 *
 * Affiche le gagnant actuel et les 3 précédents vainqueurs du vote
 * "Prix malbouffe de la semaine" (offres D/E les plus votées).
 * Pour la semaine actuelle : top 3 + vote de l'utilisateur.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { app, analytics } from '../index';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import './PrixMalbouffe.css';
import NutriScoreBadge from './NutriScoreBadge';

const JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY = 'junkFoodPrizeVote';

function getISOWeek(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/** Retourne le lundi de la semaine ISO (ex. "2026-W11" → Date du lundi) */
function getMondayOfISOWeek(weekId) {
  const match = String(weekId).match(/^(\d{4})-W(\d{2})$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // 1 = lundi, 7 = dimanche
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  return monday;
}

/** Retourne les 4 semaines : courante + 3 précédentes */
function getWeeksToFetch() {
  const weeks = [];
  const now = new Date();
  for (let i = 0; i < 4; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - 7 * i);
    weeks.push(getISOWeek(d));
  }
  return weeks;
}

/** Agrège les votes par offerId et retourne le gagnant (plus de votes) */
function getWinnerFromVotes(docs) {
  const top3 = getTop3FromVotes(docs);
  return top3[0] || null;
}

/** Agrège les votes et retourne le top 3 (pour la semaine actuelle) */
function getTop3FromVotes(docs) {
  const byOffer = new Map();
  docs.forEach((docSnap) => {
    const d = docSnap.data();
    const offerId = d.offerId || d.offerUrl || '';
    if (!offerId) return;
    const offerUrl = d.offerUrl || (typeof offerId === 'string' && offerId.startsWith('http') ? offerId : null);
    const existing = byOffer.get(offerId) || { count: 0, company: d.company, position: d.position, offerUrl };
    existing.count += 1;
    byOffer.set(offerId, existing);
  });
  const sorted = [...byOffer.entries()]
    .map(([offerId, val]) => ({ ...val, offerId, voteCount: val.count }))
    .sort((a, b) => b.voteCount - a.voteCount);
  return sorted.slice(0, 3);
}

const MEDAL_EMOJI = { 1: '🥇', 2: '🥈', 3: '🥉' };

function WinnerEntry({ entry, week, rank, t, isWinner }) {
  const medal = MEDAL_EMOJI[rank] || String(rank);
  return (
    <div className={`prix-malbouffe-entry ${isWinner ? 'prix-malbouffe-entry-winner' : ''}`}>
      <span className="prix-malbouffe-rank" aria-label={`${rank}e`}>{medal}</span>
      <div className="prix-malbouffe-score-cell">
        <NutriScoreBadge score={2} size={56} />
      </div>
      <div className="prix-malbouffe-winner-info">
        <p className="prix-malbouffe-company">{entry.company || t('PrixMalbouffeUnknownCompany')}</p>
        <p className="prix-malbouffe-position">{entry.position || ''}</p>
        <p className="prix-malbouffe-votes">{entry.voteCount} {t(entry.voteCount === 1 ? 'PrixMalbouffeVoteCount' : 'PrixMalbouffeVotesCount')}</p>
        {entry.offerUrl && (
          <a
            href={entry.offerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="prix-malbouffe-offer-link"
            onClick={() => logEvent(analytics, 'prix_malbouffe_offer_click', { week, company: entry.company, rank })}
          >
            {t('PrixMalbouffeViewOffer')}
          </a>
        )}
      </div>
    </div>
  );
}

function WinnerCard({ winner, week, weekLabel, rank, top3, t }) {
  if (!winner && (!top3 || top3.length === 0)) {
    return (
      <div className="prix-malbouffe-card prix-malbouffe-card-empty">
        <span className="prix-malbouffe-week">{weekLabel}</span>
        <p className="prix-malbouffe-empty">{t('PrixMalbouffeNoVotes')}</p>
      </div>
    );
  }
  return (
    <div className={`prix-malbouffe-card ${rank === 1 ? 'prix-malbouffe-card-winner' : ''}`}>
      <span className="prix-malbouffe-week">{weekLabel}</span>
      {top3 && top3.length > 0 ? (
        <div className="prix-malbouffe-top3">
          {top3.map((entry, idx) => (
            <WinnerEntry key={entry.offerId} entry={entry} week={week} rank={idx + 1} t={t} isWinner={idx === 0} />
          ))}
        </div>
      ) : (
        <div className="prix-malbouffe-winner-content">
          <NutriScoreBadge score={2} size={72} />
          <div className="prix-malbouffe-winner-info">
            <p className="prix-malbouffe-company">{winner.company || t('PrixMalbouffeUnknownCompany')}</p>
            <p className="prix-malbouffe-position">{winner.position || ''}</p>
            <p className="prix-malbouffe-votes">{winner.voteCount} {t(winner.voteCount === 1 ? 'PrixMalbouffeVoteCount' : 'PrixMalbouffeVotesCount')}</p>
            {winner.offerUrl && (
              <a
                href={winner.offerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="prix-malbouffe-offer-link"
                onClick={() => logEvent(analytics, 'prix_malbouffe_offer_click', { week, company: winner.company })}
              >
                {t('PrixMalbouffeViewOffer')}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function getJunkFoodPrizeVoteStatus() {
  try {
    const raw = localStorage.getItem(JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const currentWeek = getISOWeek();
    if (data.week !== currentWeek) return null;
    return { week: data.week, offerId: data.offerId, company: data.company, position: data.position, offerUrl: data.offerUrl };
  } catch {
    return null;
  }
}

function PrixMalbouffe() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [winners, setWinners] = useState([]);
  const [userVote, setUserVote] = useState(null);

  const currentWeek = getISOWeek();

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    const voteWeek = params.vote_week;
    const voteOfferId = params.vote_offerId;
    if (voteWeek && voteOfferId && voteWeek === currentWeek) {
      const voteData = {
        week: voteWeek,
        offerId: voteOfferId,
        company: params.vote_company || null,
        position: params.vote_position || null,
        offerUrl: (params.vote_offerUrl || (voteOfferId.startsWith('http') ? voteOfferId : null)) || null,
      };
      try {
        localStorage.setItem(JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY, JSON.stringify(voteData));
        setUserVote(voteData);
        const newParams = new URLSearchParams(searchParams);
        ['vote_week', 'vote_offerId', 'vote_company', 'vote_position'].forEach((k) => newParams.delete(k));
        const newSearch = newParams.toString();
        const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      } catch (e) {
        console.error('PrixMalbouffe: failed to store vote from URL', e);
      }
    }
  }, [searchParams, currentWeek]);

  useEffect(() => {
    setUserVote(getJunkFoodPrizeVoteStatus());
  }, [winners]);

  const fetchWinners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const db = getFirestore(app);
      const weeks = getWeeksToFetch();
      const results = [];
      for (const week of weeks) {
        const q = query(
          collection(db, 'junkFoodPrizeVotes'),
          where('week', '==', week)
        );
        const snapshot = await getDocs(q);
        if (week === currentWeek) {
          const top3 = getTop3FromVotes(snapshot.docs);
          results.push({ week, winner: top3[0] || null, top3 });
        } else {
          const winner = getWinnerFromVotes(snapshot.docs);
          results.push({ week, winner });
        }
      }
      setWinners(results);
    } catch (err) {
      console.error('PrixMalbouffe fetch error:', err);
      setError(err?.message || t('PrixMalbouffeError'));
    } finally {
      setLoading(false);
    }
  }, [t, currentWeek]);

  useEffect(() => {
    fetchWinners();
  }, [fetchWinners]);

  useEffect(() => {
    logEvent(analytics, 'page_view', { page_title: 'Prix malbouffe', page_location: window.location.pathname });
  }, []);

  const formatWeekLabel = (weekId) => {
    const monday = getMondayOfISOWeek(weekId);
    if (!monday) return weekId;
    const locale = (i18n.language || 'fr').startsWith('fr') ? 'fr-FR' : 'en';
    const dateStr = monday.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    return t('PrixMalbouffeWeekLabel', { date: dateStr });
  };

  const pageUrl = `https://job-nutriscore.eu/${lang || 'fr'}/prix-malbouffe`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;

  return (
    <div className="prix-malbouffe-page">
      <h2 className="prix-malbouffe-title">{t('PrixMalbouffePageTitle')}</h2>
      <p className="prix-malbouffe-description">{t('PrixMalbouffePageDescription')}</p>

      {userVote && (
        <div className="prix-malbouffe-user-vote">
          <h3 className="prix-malbouffe-user-vote-title">{t('PrixMalbouffeYourVote')}</h3>
          <p className="prix-malbouffe-user-vote-info">
            {userVote.company || t('PrixMalbouffeUnknownCompany')}{userVote.position ? ` – ${userVote.position}` : ''}
          </p>
          {(userVote.offerUrl || userVote.offerId?.startsWith('http')) && (
            <a
              href={userVote.offerUrl || userVote.offerId}
              target="_blank"
              rel="noopener noreferrer"
              className="prix-malbouffe-offer-link"
            >
              {t('PrixMalbouffeViewOffer')}
            </a>
          )}
        </div>
      )}

      {loading ? (
        <p className="prix-malbouffe-loading">{t('PrixMalbouffeLoading')}</p>
      ) : error ? (
        <p className="prix-malbouffe-error">{error}</p>
      ) : (
        <div className="prix-malbouffe-winners">
          {winners.map(({ week, winner, top3 }, idx) => (
            <WinnerCard
              key={week}
              winner={winner}
              week={week}
              weekLabel={formatWeekLabel(week)}
              rank={idx + 1}
              top3={top3}
              t={t}
            />
          ))}
        </div>
      )}

      <div className="prix-malbouffe-share-row">
        <a
          href={linkedInShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="prix-malbouffe-share-linkedin"
          onClick={() => logEvent(analytics, 'prix_malbouffe_share_linkedin', {})}
        >
          👉 {t('PrixMalbouffeShareButton')}
        </a>
        <a
          href="https://www.linkedin.com/pulse/our-big-techs-regulation-innovation-professions-europe-giraudel-r7bve/"
          target="_blank"
          rel="noopener noreferrer"
          className="prix-malbouffe-share-linkedin"
          onClick={() => logEvent(analytics, 'prix_malbouffe_changeons_ca_click', {})}
        >
          {t('PrixMalbouffeChangeonsCa')}
        </a>
      </div>
    </div>
  );
}

export default PrixMalbouffe;
